import React from 'react';
import { Images } from 'utils';

/**
 * Shared presentational building blocks for the setup guides so Office Ally and
 * Availity render identically to Figma (node 34892-118847). Pure display.
 */

/**
 * A numbered step: blue circle + connecting rail on the left, "Step N - <title>"
 * heading, optional bold subtitle, then body content.
 */
export const GuideStep = ({ index, title, subtitle, children }) => (
    <div className="guide-step">
        <div className="guide-step-rail">
            <span className="guide-step-circle">{index}</span>
            <span className="guide-step-line" />
        </div>
        <div className="guide-step-content">
            <p className="guide-step-heading">
                Step {index} - {title}
            </p>
            {subtitle && <p className="guide-step-subheading">{subtitle}</p>}
            {children}
        </div>
    </div>
);

/**
 * Left-accent callout box. tone: 'info' | 'neutral' | 'warning' | 'success'.
 * Pass `icon` (an Images.* src) to show a leading icon; 'info' (Before you start)
 * has none in the design.
 */
export const GuideCallout = ({ tone = 'info', icon, title, children, imageStyles={} }) => (
    <div className={`guide-callout guide-callout--${tone}`}>
        {icon && <img src={icon} alt="" className="guide-callout-icon" style={{ ...imageStyles }} />}
        <div className="guide-callout-content">
            {title && <p className="guide-callout-title">{title}</p>}
            <div className="guide-callout-body">{children}</div>
        </div>
    </div>
);

/** Plain heading block (Complete the connection / Having trouble connecting?) with a top divider. */
export const GuideSection = ({ title, children }) => (
    <div className="guide-section">
        {title && <p className="guide-section-title">{title}</p>}
        {children}
    </div>
);

/**
 * Screenshot slot. Renders the image when `src` is provided, otherwise a labeled
 * placeholder so the guide reads correctly before the Figma exports are dropped in.
 */
export const GuideImage = ({ src, alt, caption, style={} }) => (
    <figure className="guide-image">
        {src ? (
            <img src={src} alt={alt || caption || ''} style={{ ...style}} />
        ) : (
            <div className="guide-image-placeholder">
                <span>Screenshot{caption ? `: ${caption}` : ''}</span>
            </div>
        )}
    </figure>
);

/** Outlined CTA rendered as an anchor (mailto / external link) with a leading icon. */
export const GuideLinkButton = ({ href, icon = Images.mail, external, children }) => (
    <a
        className="guide-link-btn"
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
        {icon && <img src={icon} alt="" />}
        {children}
    </a>
);
